import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

function AdminProxyConfig() {
  const [config, setConfig] = useState({ relayServer: '', relayUsername: '', relayPassword: '' });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [reloadMsg, setReloadMsg] = useState('');
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('adminToken') || localStorage.getItem('token');

  useEffect(() => {
    fetch(`${API_URL}/admin/proxy-config`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.config) setConfig(data.config);
        setLoading(false);
      })
      .catch(() => { setError('Impossible de charger la configuration'); setLoading(false); });
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await fetch(`${API_URL}/admin/proxy-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (data.success) setSaved(true);
      else setError(data.error || 'Erreur lors de la sauvegarde');
    } catch { setError('Erreur réseau'); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/proxy-config/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      setTestResult(data);
    } catch { setTestResult({ success: false, message: '❌ Erreur réseau' }); }
    setTesting(false);
  };

  const handleReload = async () => {
    setReloading(true); setReloadMsg(''); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/proxy-config/reload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setReloadMsg('✅ Backend rechargé avec succès. Les nouvelles valeurs sont actives.');
      } else {
        setError(data.error || 'Erreur lors du rechargement');
      }
    } catch { setError('Erreur réseau lors du rechargement'); }
    setReloading(false);
  };

  if (loading) return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-loading">Chargement...</div>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">⚙️ Configuration Proxy HLS</h1>
          <p className="admin-page-subtitle">
            Serveur source utilisé pour proxifier les streams vers les clients
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {saved && <div className="alert alert-success">✅ Configuration sauvegardée</div>}
        {reloadMsg && <div className="alert alert-success">{reloadMsg}</div>}

        <div className="admin-card">
          <h2 className="card-title">🔌 Serveur source (fournisseur)</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Ce serveur est utilisé comme source pour les flux HLS. Notre serveur fetche 
            les streams depuis lui et les re-transmet aux clients.
          </p>

          <div className="form-group">
            <label className="form-label">URL du serveur source</label>
            <input
              className="form-input"
              type="url"
              placeholder="http://51.254.135.114"
              value={config.relayServer}
              onChange={e => setConfig({ ...config, relayServer: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Nom d'utilisateur</label>
            <input
              className="form-input"
              type="text"
              placeholder="username"
              value={config.relayUsername}
              onChange={e => setConfig({ ...config, relayUsername: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Mot de passe</label>
            <input
              className="form-input"
              type="text"
              placeholder="password"
              value={config.relayPassword}
              onChange={e => setConfig({ ...config, relayPassword: e.target.value })}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>
              {testing ? '⏳ Test en cours...' : '🔍 Tester la connexion'}
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
            </button>
          </div>

          {testResult && (
            <div className={`alert ${testResult.success ? 'alert-success' : 'alert-error'}`} style={{ marginTop: '1rem' }}>
              <strong>{testResult.message}</strong>
              {testResult.info && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                  <div>Statut : {testResult.info.status}</div>
                  <div>Connexions : {testResult.info.activeConnections} / {testResult.info.maxConnections}</div>
                  <div>Expire le : {testResult.info.expDate}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="admin-card" style={{ marginTop: '1.5rem', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
          <h2 className="card-title">🔄 Recharger le backend</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Après avoir sauvegardé la configuration, rechargez le backend pour appliquer 
            les nouvelles valeurs sans interruption de service (PM2 reload).
          </p>
          <button
            className="btn btn-danger"
            onClick={handleReload}
            disabled={reloading}
          >
            {reloading ? '⏳ Rechargement...' : '🔄 Recharger le backend maintenant'}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminProxyConfig;
